import { useContext, useEffect, useReducer } from "react";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import { sendRequest } from "@/common/http";
import translate from "@/i18n/translate";
import { WorkFlowTaskProgress } from "./workflowTask/WorkflowTaskProgress";
import ApiError from "@/features/common/elements/ApiError";

interface State {
  workflowTasks: WorkflowTask[] | null;
  apiError: boolean;
}
interface Action {
  type: string;
  workflowTasks: WorkflowTask[] | null;
  apiError: boolean;
}
export interface Step {
  id: string;
  name: string;
  state: string;
  stepIndex: number;
  assignees: Assignee[];
  submittedDate: string | null;
  dueDate: string | null;
  daysUntilDue: number | null;
  dueDateType: string;
  sentBackTo: string;
  skippedTo: string;
  splitStep: boolean;
  submissionUrl: string;
}

export interface Assignee {
  firstName: string;
  lastName: string;
  middleName: string | null;
  orgId: string;
  status?: string;
  submittedDate?: string;
  dueDate?: string;
}

export interface WorkflowTask {
  currentStepId: string;
  currentStepIndex: number;
  id: string;
  status: string;
  steps: Step[];
  title: string;
  totalSteps: number;
}
const defaultAction: Action = {
  type: "",
  workflowTasks: null,
  apiError: false
};

const initialState: State = {
  workflowTasks: null,
  apiError: false
};

const stateReducer = (state: State, action: Action) => {
  switch (action.type) {
    case "getWorkflowTasks":
      return {
        ...state,
        workflowTasks: action.workflowTasks
      };
    case "setApiError":
      return {
        ...state,
        apiError: true
      };
    default:
      return state;
  }
};

export function ReviewsInProgress() {
  const { sysUser, institution } = useContext<AppContextInterface>(AppContext);
  const [state, dispatch] = useReducer(stateReducer, initialState);
  useEffect(() => {
    const applyResult = (result: WorkflowTask[]) => {
      dispatch({
        ...defaultAction,
        type: "getWorkflowTasks",
        workflowTasks: result
      });
    };
    const onError = () => {
      dispatch({
        ...defaultAction,
        type: "setApiError"
      });
    };
    sendRequest(
      {
        uri: `/faculty_success/rpt-workflow-tasks?sfid=${institution?.salesforceId}`,
        camelize: true
      },
      applyResult,
      onError
    );
  }, []);

  return (
    (state.workflowTasks || state.apiError) && (
      <div className="widget-card paper-card">
        <div className="card-header header">
          <h3 className="title">
            {translate("app.home.reviewsInProgress")}
          </h3>
          <div className="base-font">
            {translate("app.home.reviewsInProgress.desc")}
          </div>
        </div>
        <div className="body">
          <div className="widget-body">
            {state.apiError && (
              <div className="api-error-wrapper">
                <ApiError containerClasses="api-error" />
              </div>
            )}

            {state.workflowTasks && state.workflowTasks.length === 0 && (
              <ReviewInProgressEmptyState />
            )}
            {state.workflowTasks &&
              state.workflowTasks.length > 0 &&
              state.workflowTasks.map((workflowTask: WorkflowTask) => {
                return (
                  <WorkFlowTaskProgress
                    key={workflowTask?.id}
                    workflowTask={workflowTask}
                    totalWorkflows={state.workflowTasks?.length || 0}
                  />
                );
              })}
          </div>
        </div>
      </div>
    )
  );
}

export function ReviewInProgressEmptyState() {
  return (
    <div className="flex-row empty-state-reviews">
      <div className="empty-state-msg">
        {translate("app.home.reviewsInProgress.emptyState")}
      </div>
    </div>
  );
}
